import ObjectUtil from "../../core/internal/util/ObjectUtil"
import { HackleApp } from "./HackleWebAppClient"
import Logger from "../../core/internal/logger"

export interface WebViewConfig {
  automaticScreenTracking: boolean
  automaticEngagementTracking: boolean
}

const DEFAULT_CONFIG: WebViewConfig = {
  automaticScreenTracking: false,
  automaticEngagementTracking: false
}

const log = Logger.log

export class HackleWebViewConfigurations {
  constructor(private readonly configResolver: HackleWebViewConfigurationResolver) {}

  config(): WebViewConfig {
    const webAppConfig = this.configResolver.resolve()

    return {
      ...DEFAULT_CONFIG,
      ...webAppConfig
    }
  }
}

export class HackleWebViewConfigurationResolver {
  constructor(private readonly hackleApp: HackleApp) {}

  private supports(hackleApp: HackleApp): hackleApp is HackleApp & { getWebViewConfig: () => string } {
    return "getWebViewConfig" in hackleApp && ObjectUtil.isNotNullOrUndefined(hackleApp.getWebViewConfig)
  }

  private resolveAutomaticEngagementTracking(parsedConfig: Partial<WebViewConfig>): boolean {
    if (typeof parsedConfig.automaticEngagementTracking !== "boolean") {
      return DEFAULT_CONFIG.automaticEngagementTracking
    }

    return parsedConfig.automaticEngagementTracking
  }

  private resolveAutomaticScreenTracking(parsedConfig: Partial<WebViewConfig>): boolean {
    if (typeof parsedConfig.automaticScreenTracking !== "boolean") {
      return DEFAULT_CONFIG.automaticScreenTracking
    }

    return parsedConfig.automaticScreenTracking
  }

  resolve(): WebViewConfig {
    try {
      if (!this.supports(this.hackleApp)) return DEFAULT_CONFIG

      const serializedConfig = this.hackleApp.getWebViewConfig()
      const parsedConfig: Partial<WebViewConfig> = JSON.parse(serializedConfig)

      return {
        automaticEngagementTracking: this.resolveAutomaticEngagementTracking(parsedConfig),
        automaticScreenTracking: this.resolveAutomaticScreenTracking(parsedConfig)
      }
    } catch (err) {
      log.warn(`Failed to resolve web view config: ${err}`)
      return DEFAULT_CONFIG
    }
  }
}
